import React, { useState } from 'react';
import Papa from 'papaparse';
import { useApiCaller } from '../../utils/ApiUtilities';
import { useError } from '../ErrorContext';
import { getIngestionUploadUrl } from '../../utils/UrlUtilities';
import { constructCsvUploadPayload } from '../../utils/PayloadUtils';
import { TAGLEVELS } from '../../utils/Constants';

// The 14 question columns we read from each CSV row. `source_file` in the CSV is
// ignored — we use the uploaded filename as the job's source instead.
const CSV_FIELDS = [
    'question_text', 'option_a', 'option_b', 'option_c', 'option_d',
    'correct_option', 'answer_explanation', 'question_type', 'text_answer',
    'level', 'subject', 'topic', 'exam', 'board', 'division', 'unit',
    'bcs_term', 'difficulty', 'year', 'side_text', 'audio_url', 'solving_time_seconds',
    'question_image_url', 'option_a_image_url', 'option_b_image_url',
    'option_c_image_url', 'option_d_image_url', 'explanation_image_url',
];

const clean = (v) => (v == null ? '' : String(v).trim());

// Parse one File into { source_file, questions:[...] }. PapaParse auto-detects the
// delimiter (comma OR tab) and handles quoted fields / Bangla / BOM. Rows without
// question_text are dropped.
function parseCsvFile(file) {
    return new Promise((resolve, reject) => {
        Papa.parse(file, {
            header: true,
            skipEmptyLines: true,
            transformHeader: (h) => clean(h).toLowerCase(),
            complete: (results) => {
                const questions = (results.data || [])
                    .map((row) => {
                        const q = {};
                        for (const f of CSV_FIELDS) q[f] = clean(row[f]);
                        return q;
                    })
                    .filter((q) => q.question_text);
                resolve({ source_file: file.name, questions });
            },
            error: (err) => reject(err),
        });
    });
}

// CSV upload: pick one or more CSV files + a level, parse them client-side into
// { source_file, questions[] } and POST as JSON. Downstream (jobs → drafts →
// review/approve) is unchanged. CSV ingestion is synchronous — no polling.
const IngestionUploadForm = ({ onJobComplete }) => {
    const CallApi = useApiCaller();
    const { showError, showSuccess } = useError();
    const [files, setFiles] = useState([]); // File[]
    const [level, setLevel] = useState('');
    const [parsing, setParsing] = useState(false);

    const handleFilesChange = (e) => {
        setFiles(Array.from(e.target.files || []));
    };

    const handleSubmit = async () => {
        if (!level || files.length === 0) return;
        setParsing(true);
        let parsed;
        try {
            parsed = await Promise.all(files.map(parseCsvFile));
        } catch (err) {
            setParsing(false);
            showError(`Could not read CSV: ${err?.message || 'parse error'}`);
            return;
        }

        const totalRows = parsed.reduce((n, f) => n + f.questions.length, 0);
        if (totalRows === 0) {
            setParsing(false);
            showError('No question rows found. Ensure the CSV has a header row and a question_text column.');
            return;
        }

        const res = await CallApi({
            url: getIngestionUploadUrl(),
            method: 'POST',
            payload: constructCsvUploadPayload(level, parsed),
            token: true,
        });
        setParsing(false);
        if (!res.success) return;

        const jobs = res.data.jobs || [];
        const created = jobs.reduce((n, j) => n + (j.total || 0), 0);
        showSuccess(
            `Parsed ${created} question(s) from ${jobs.length} file(s). Switch to the Drafts tab to review.`
        );
        setFiles([]);
        // Flip the page to the Drafts tab for the last job created (matches the old flow).
        const last = jobs[jobs.length - 1];
        if (onJobComplete && last) {
            onJobComplete({ job_id: last.job_id, status: 'DONE' });
        }
    };

    const submitDisabled = parsing || !level || files.length === 0;

    return (
        <div className="card mb-4">
            <div className="card-body">
                <h5 className="card-title mb-3">Import Questions from CSV</h5>

                <div className="mb-3">
                    <label className="form-label">CSV file(s) *</label>
                    <input
                        type="file"
                        accept=".csv,text/csv,text/tab-separated-values"
                        className="form-control"
                        multiple
                        onChange={handleFilesChange}
                        disabled={parsing}
                    />
                    <small className="form-text text-muted">
                        Comma- or tab-separated. Header row required with columns:{' '}
                        question_text, option_a–d, correct_option, answer_explanation, level,
                        subject, topic, exam, bcs_term, difficulty, year. You can select multiple files.
                    </small>
                    {files.length > 0 && (
                        <small className="form-text d-block text-muted">
                            Selected: {files.map(f => f.name).join(', ')}
                        </small>
                    )}
                </div>

                <div className="mb-3">
                    <label className="form-label">Level *</label>
                    <select
                        className="form-select"
                        value={level}
                        onChange={e => setLevel(e.target.value)}
                        disabled={parsing}
                    >
                        <option value="">Select a level</option>
                        {TAGLEVELS.map(item => item.submenu
                            ? <optgroup key={item.value} label={item.label}>{item.submenu.map(sub => <option key={sub.value} value={sub.value}>{sub.label}</option>)}</optgroup>
                            : <option key={item.value} value={item.value}>{item.label}</option>
                        )}
                    </select>
                    <small className="form-text text-muted">
                        Used for rows with no <code>level</code> column, and to validate subject/topic/exam tags.
                    </small>
                </div>

                <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleSubmit}
                    disabled={submitDisabled}
                >
                    {parsing ? 'Parsing…' : 'Parse Question From CSV'}
                </button>
            </div>
        </div>
    );
};

export default IngestionUploadForm;
